import { Modal } from 'baseui/modal';
import { pick } from 'lodash';
import React, { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import styled from 'styled-components';
import { useEvents } from '../../../Components/Events/EventsProvider';
import { FinalButton } from '../../../Components/FinalButton';
import { FormControl, Input } from '../../../Components/Form';
import { Modal as ModalStyle, Text } from '../../../globalStyles';
import { ResourceType, useCreateOrUpdateOrganizationResourceMutation } from '../../../graphQL';
import { BeaconResourceForm } from '../components/BeaconResourceForm';
import { CampusResourceForm } from '../components/CampusResourceForm';
import { ResourceDisplay } from '../components/ResourceDisplay';
import { BEACON_RESOURCE_METADATA, isBeaconResource } from '../constants/beaconResourceMetadata';
import { FormFields, Resource, ResourceModalProps } from '../types';
import { getResourceDisplayName } from '../utils';
import { ConfirmDeleteModal } from './ConfirmDeleteModal';

export function ResourceModal({
  organizationId,
  resource,
  onClose,
  refetch,
  modalResourceType,
}: ResourceModalProps) {
  const fieldNames = [
    'name',
    'contact',
    'location',
    'phone',
    'email',
    'hours',
    'website',
    'description',
    'showAsCrisisResource',
  ] as const;
  const { track } = useEvents();
  const [mutate] = useCreateOrUpdateOrganizationResourceMutation({
    onCompleted: d => {
      if (resource?.id) {
        track('campus-resource.edited', {
          organizationId,
          resourceId: d.createOrUpdateOrganizationResource.id,
        });
      } else {
        track('campus-resource.added', {
          organizationId,
          resourceId: d.createOrUpdateOrganizationResource.id,
        });
      }
    },
  });
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [resourceCrisis, setResourceCrisis] = useState(resource?.showAsCrisisResource);
  const form = useForm<FormFields>({
    defaultValues: {
      tags: [],
      ...pick(resource, fieldNames),
    },
  });

  let resourceType: ResourceType;
  if (resource) {
    resourceType = resource.resourceType;
  } else if (modalResourceType) {
    resourceType = modalResourceType;
  } else {
    return null;
  }

  const onSubmit = form.handleSubmit(async values => {
    setSaving(true);
    try {
      await mutate({
        variables: {
          organizationId,
          resource: {
            id: resource?.id,
            resourceType,
            showAsCrisisResource: resourceCrisis,
            tags: [],
            ...pick(values, fieldNames),
          },
          // CscHandbook - This will be fixed once CscHandbook feature flag is removed. passing [] for tags cause problems otherwise
        },
      });
      await refetch();
      onClose();
    } finally {
      setSaving(false);
    }
  });
  const fields = form.watch();

  return (
    <Modal isOpen unstable_ModalBackdropScroll onClose={onClose}>
      {confirmDelete && resource && (
        <ConfirmDeleteModal
          resourceId={resource.id}
          organizationId={organizationId}
          onClose={() => setConfirmDelete(false)}
          onDelete={onClose}
          refetch={refetch}
        />
      )}
      <ModalStyle.body>
        <FormProvider {...form}>
          <div className="mt4">
            <Text.h4>
              {resource ? 'Edit' : 'Add'}{' '}
              {isBeaconResource(resourceType) ? 'Beacon Resource' : 'Campus Resource'}
            </Text.h4>
            <Text.h2 className="mb2">{getResourceDisplayName(resourceType)}</Text.h2>
            {isBeaconResource(resourceType) && BEACON_RESOURCE_METADATA[resourceType] && (
              <Text.bodySmallGrey className="mb4">
                {BEACON_RESOURCE_METADATA[resourceType]?.description}
              </Text.bodySmallGrey>
            )}
          </div>
          <FormControl name="name" label="Resource Name" required>
            <Input name="name" ref={form.register({ required: true })} />
          </FormControl>

          {isBeaconResource(resourceType) ? (
            <BeaconResourceForm register={form.register} />
          ) : (
            <CampusResourceForm
              register={form.register}
              resourceCrisis={resourceCrisis}
              setResourceCrisis={setResourceCrisis}
            />
          )}
          <hr className="mt4 mb4" />
          <Text.h2 className="mb2">
            Preview {isBeaconResource(resourceType) ? 'Beacon Resource' : 'Campus Resource'}
          </Text.h2>
          <Text.bodySmallGrey>
            View how this resource will be displayed to students.
          </Text.bodySmallGrey>
          <PreviewBox>
            {fields.name ? (
              <ResourceDisplay resource={fields as Resource} />
            ) : (
              <Text.bodyGrey className="i">
                Fill out the fields above to view how this{' '}
                {isBeaconResource(resourceType) ? 'beacon resource' : 'campus resource'} will be
                displayed to patients.
              </Text.bodyGrey>
            )}
          </PreviewBox>
          <FinalButton
            className="w-100"
            onClick={onSubmit}
            loading={saving}
            disabled={!fields.name}
          >
            Save
          </FinalButton>
          {resource && (
            <FinalButton
              kind="minimal_danger"
              className="w-100 mt2"
              onClick={() => setConfirmDelete(true)}
            >
              Delete
            </FinalButton>
          )}
        </FormProvider>
      </ModalStyle.body>
    </Modal>
  );
}

const PreviewBox = styled.div`
  background-color: rgba(208, 225, 253, 1);
  padding: 20px;
  border-radius: 4px;
  white-space: pre-wrap;
  margin: 20px 0px 30px 0px;
`;
